// src/analytics/analytics.ts
import {useEffect} from "react";
import {useLocation} from "react-router-dom";
import {readConsent} from "./consent";

declare global {
    interface Window {
        dataLayer?: any[];
        gtag?: (...args: any[]) => void;
    }
}

const MEASUREMENT_ID = import.meta.env.VITE_GA_ID;

export function Analytics() {
    const location = useLocation();

    // Load gtag once
    useEffect(() => {
        if (!MEASUREMENT_ID) {
            if (import.meta.env.DEV) {
                console.warn("GA4 measurement ID missing (VITE_GA_ID).");
            }
            return;
        }
        if (window.gtag) return; // already loaded

        // 1) Load the GA4 script
        const s = document.createElement("script");
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
        document.head.appendChild(s);

        // 2) Init dataLayer + gtag
        const dataLayer = (window.dataLayer = window.dataLayer || []);
        window.gtag = function () {
            dataLayer.push(arguments);
        };
        window.gtag("js", new Date());

        // 3) Consent Mode v2 defaults — denied until the user opts in via the
        // cookie banner. Seed from any previously stored choice so a returning
        // visitor's consent is respected on first load.
        const stored = readConsent();
        const marketing = stored?.marketing === true ? "granted" : "denied";
        window.gtag("consent", "default", {
            ad_storage: marketing,
            ad_user_data: marketing,
            ad_personalization: marketing,
            analytics_storage: stored?.analytics ? "granted" : "denied",
            wait_for_update: 500,
        });

        // 4) Configure GA4
        window.gtag("config", MEASUREMENT_ID, {
            send_page_view: false, // we handle SPA page views manually
        });
    }, []);

    // Track page views on route change
    useEffect(() => {
        if (!MEASUREMENT_ID || !window.gtag) return;
        window.gtag("event", "page_view", {
            page_title: document.title,
            page_location: window.location.href,
            page_path: location.pathname + location.search,
        });
    }, [location.pathname, location.search]);

    return null;
}
